import { TabletButton } from '@campfire/tablet-button';
import { Box } from '@material-ui/core';
import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { initTaskFormFormikValues, TaskFormValuesType } from './model/task-form-init';
import { dynamicValidationSchema } from './model/validation-schema';
import { TaskFormItem } from './model/__generated__/TaskFormItem';
import { TaskFormFieldList } from './TaskFormFieldList';
import { FormPageNav } from './FormPageNav';
import { filterHiddenTaskItems, splitTaskItemsIntoPages } from './paged-task-items';
import { FormikErrorFocus } from '../FormikErrorFocus';
import { deserializeTaskItems } from '../../../screens/admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';

interface Props {
  onSubmit: (values: TaskFormValuesType) => void;
  taskItems: Array<TaskFormItem>;
  completedTasks?: Array<any>; // Array of completed tasks for cross-task field lookups
  volunteerProgramIds?: string[]; // Program IDs the volunteer is enrolled in (for filtering hidden fields)
}

const TaskForm = (props: Props) => {
  const { onSubmit, taskItems, completedTasks, volunteerProgramIds } = props;
  const [currentPage, setCurrentPage] = useState(0);

  const initialFormikValues = useMemo(() => {
    if (taskItems.length === 0) return undefined;
    return initTaskFormFormikValues(taskItems, undefined);
  }, [taskItems]);

  const pages = useMemo(() => splitTaskItemsIntoPages(filterHiddenTaskItems(taskItems as any[], volunteerProgramIds)), [
    taskItems,
    volunteerProgramIds,
  ]);

  const validationSchema = dynamicValidationSchema(deserializeTaskItems(taskItems), undefined, volunteerProgramIds);

  const isPaged = pages.length > 1;
  const currentPageItems = pages[currentPage]?.items ?? [];

  return initialFormikValues ? (
    <Formik initialValues={initialFormikValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ values }) => {
        return (
          <Form>
            <TaskFormFieldList
              taskItems={deserializeTaskItems((isPaged ? currentPageItems : taskItems) as any)}
              values={values}
              completedTasks={completedTasks}
              volunteerProgramIds={volunteerProgramIds}
            />
            {isPaged ? (
              <Box width={1} display='flex' justifyContent='center'>
                <Box width={720}>
                  <FormPageNav
                    pages={pages}
                    currentPage={currentPage}
                    onBack={() => setCurrentPage((page) => Math.max(0, page - 1))}
                    onNext={() => setCurrentPage((page) => Math.min(pages.length - 1, page + 1))}
                    onGoToPage={setCurrentPage}
                  />
                </Box>
              </Box>
            ) : (
              <Box width={1} display='flex' justifyContent='center'>
                <Box width={720} display='flex' justifyContent='flex-start'>
                  <TabletButton color='primary' variant='contained' type='submit' size='large' style={{ width: 256 }}>
                    Submit
                  </TabletButton>
                </Box>
              </Box>
            )}
            <FormikErrorFocus />
          </Form>
        );
      }}
    </Formik>
  ) : null;
};

export { TaskForm };
